/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — edit-session commit client.

   People Rebuild §0/§3 (Josh grant 2026-08-17, "edit-state + save
   model"): the editor accumulates ALL changes locally during a session
   — node moves, reports-to changes, edge/junction create-edit-delete —
   then commits ONCE via the `orgchart-commit` server action (one
   transaction, applied atomically). This kills the sequential
   whole-row-save-vs-loadAll-refetch race class permanently: there is no
   window where a partial edit session is visible to a concurrent
   refetch.

   Direct POST to /api, same pattern as `saveBatchedPositions` in
   batchedPositionSave.ts — this bypasses cloudCache's generic
   table-save path entirely (like that helper does) because the payload
   isn't a single-table {table, record} CRUD call, it's a multi-table
   batch the generic cache layer has no shape for.
   ═══════════════════════════════════════════════════════════════ */
import { getAuthToken } from '../../../services/authService';
import { API_URL } from '../../../lib/apiBase';
import { invalidate } from '../../../services/cloudCache';
import type { OrgChartEdge, OrgChartJunction, OrgChartNote } from '../../../../shared/entities';

// Every table a commit can touch. Because this POST bypasses cloudCache,
// the cache's list entries for these tables are NOT write-through
// invalidated — and org_chart_view_positions is never broadcast either
// (composite PK, no id for the realtime cache path). Without an explicit
// invalidate, the caller's post-commit refresh is served the STALE cached
// lists and freshly committed board content renders empty until an app
// restart (found live 2026-08-17: "Duplicate view" produced a
// seemingly-empty copy).
const COMMIT_TABLES = [
  'team_members',
  'org_chart_edges',
  'org_chart_junctions',
  'org_chart_notes',
  'org_chart_view_positions',
  // Team Management UX Overhaul (2026-08-18): a commit can now also touch
  // team membership (team_assignments) and team containment (teams.parent_
  // team_id) via on-canvas drag staging — both need the same write-through
  // invalidate as every other commit-touched table, or a post-commit
  // refreshAll() would serve stale cached rows (the exact "Duplicate view"
  // defect class the comment above already describes for the other tables).
  'team_assignments',
  'teams',
] as const;

export interface OrgChartCommitPositionUpdate {
  id: string;
  orgX: number;
  orgY: number;
}

export interface OrgChartCommitReportsToUpdate {
  id: string;
  /** New manager id, or '__root__' for "on chart but detached", or null. */
  reportsTo: string | null;
}

/** Edge upsert row — same shape as OrgChartEdge, id required (client
 *  generates ids up front, e.g. via crypto.randomUUID(), same as every
 *  other cache.save() caller does). */
export type OrgChartCommitEdgeUpsert = OrgChartEdge;

/** Junction upsert row — same shape as OrgChartJunction, id required. */
export type OrgChartCommitJunctionUpsert = OrgChartJunction;

/** Note (text-box) upsert row — same shape as OrgChartNote, id required. */
export type OrgChartCommitNoteUpsert = OrgChartNote;

/** One staged "add this member to this team" — Team Management UX
 *  Overhaul (2026-08-18): dragging an on-canvas person card into a team
 *  box, Main board only. `role` defaults server-side to 'member'. */
export interface OrgChartCommitMembershipAdd {
  teamId: string;
  memberId: string;
  role?: 'leader' | 'member';
}

/** One staged "remove this member from this team" — dragging a card that
 *  IS in a team box fully outside it. Identified by (teamId, memberId)
 *  pair, not an assignment row id — the server deletes whatever row
 *  currently matches, which is the correct semantics for "this person
 *  should no longer be in this team" regardless of which specific
 *  assignment row that was. */
export interface OrgChartCommitMembershipRemove {
  teamId: string;
  memberId: string;
}

/** One staged team-box-onto-team-box reparent (nest) or team-box-onto-
 *  empty-canvas un-nest (`parentTeamId: null`) — Main board only, the
 *  canvas equivalent of the Directory team tree's drag-to-reparent
 *  (item 2). Cycle-guarded client-side before staging AND server-side on
 *  commit (defense in depth, same guard shape as the Directory tree). */
export interface OrgChartCommitTeamParentChange {
  teamId: string;
  parentTeamId: string | null;
}

export interface OrgChartCommitPayload {
  /** Canvas-view target (migration 041, Josh grant 2026-08-17: "views
   *  become separate canvases/boards"). undefined/null = the implicit
   *  Main view (existing behavior — positions on team_members.orgX/orgY,
   *  reportsTo applied for real). Set = a custom board — positions land
   *  in org_chart_view_positions instead, reportsTo is ignored (Main is
   *  the only org-wide-truth source; see the response's
   *  `reportsToIgnored`), and every edge/junction/note upsert in this
   *  commit is stamped with that view. */
  viewId?: string | null;
  positions?: OrgChartCommitPositionUpdate[];
  /** Members dropped off THIS board this session. Only meaningful (and
   *  only applied) when `viewId` is set — a no-op for the Main commit. */
  removedMemberIds?: string[];
  reportsTo?: OrgChartCommitReportsToUpdate[];
  edges?: {
    upsert?: OrgChartCommitEdgeUpsert[];
    deleteIds?: string[];
  };
  junctions?: {
    upsert?: OrgChartCommitJunctionUpsert[];
    deleteIds?: string[];
  };
  notes?: {
    upsert?: OrgChartCommitNoteUpsert[];
    deleteIds?: string[];
  };
  /** Team membership staged via on-canvas card<->team-box drag. Main-
   *  board truth only — a commit against a custom `viewId` has these
   *  silently ignored server-side (see the response's
   *  `membershipIgnored`), never applied and never errored, same
   *  ignore-not-error pattern as `reportsTo`. */
  membership?: {
    adds?: OrgChartCommitMembershipAdd[];
    removes?: OrgChartCommitMembershipRemove[];
  };
  /** Team reparents staged via on-canvas team-box drag. Main-board truth
   *  only — see `teamParentChangesIgnored` on a custom-view commit. */
  teamParentChanges?: OrgChartCommitTeamParentChange[];
}

export interface OrgChartCommitResponse {
  success: true;
  /** Echoes the commit's target view (null = Main). */
  viewId?: string | null;
  applied: {
    positions: number;
    /** View-scoped position rows removed (0 for a Main commit). */
    positionsRemoved: number;
    reportsTo: number;
    edgesUpserted: number;
    edgesDeleted: number;
    junctionsUpserted: number;
    junctionsDeleted: number;
    notesUpserted: number;
    notesDeleted: number;
    membershipAdded: number;
    membershipRemoved: number;
    teamParentsChanged: number;
  };
  /** True when this commit targeted a custom view AND included reportsTo
   *  entries — those were silently ignored (reports_to is Main-only
   *  truth), not applied and not errored. Absent on a Main commit. */
  reportsToIgnored?: true;
  /** Same ignore-not-error pattern as `reportsToIgnored`, for a custom-
   *  view commit that included `membership` adds/removes. */
  membershipIgnored?: true;
  /** Same ignore-not-error pattern as `reportsToIgnored`, for a custom-
   *  view commit that included `teamParentChanges`. */
  teamParentChangesIgnored?: true;
}

/**
 * Commit an entire org-chart edit session in one atomic transaction.
 * Throws on any non-2xx response (including 401/403) — callers should
 * catch and surface the failure; there is no fallback path, unlike
 * `saveBatchedPositions`, because a partial local-only edit session with
 * no server route to land on isn't a "degrade gracefully" case — the
 * commit is the ONLY save path in the edit-session model.
 */
export async function commitOrgChart(
  payload: OrgChartCommitPayload
): Promise<OrgChartCommitResponse> {
  const token = getAuthToken();
  if (!token) throw new Error('not-signed-in');

  const res = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action: 'orgchart-commit', ...payload }),
  });

  if (!res.ok) {
    let reason = '';
    try {
      const body = await res.json();
      reason = body?.error || body?.reason || '';
    } catch {
      // ignore — response wasn't JSON
    }
    throw new Error(`orgchart-commit failed: ${res.status}${reason ? ` (${reason})` : ''}`);
  }

  // Success: drop the cached lists so the caller's refreshAll() fetches
  // the committed rows instead of the pre-commit cache.
  for (const t of COMMIT_TABLES) invalidate(t);

  return (await res.json()) as OrgChartCommitResponse;
}
